import React, { useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AlertCircle, LayoutGrid, Loader2, Rows3, Search, Trash2, Upload } from "lucide-react";
import { Card, CardContent } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Input } from "@cms/ui/components/input";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import { PaginationControls } from "@cms/ui/components/pagination-controls";
import { useApiQuery } from "@cms/portal-lib/hooks";
import { useListControls } from "@cms/portal-lib/list-controls";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@cms/ui/components/alert-dialog";
import { PageHeader } from "@cms/ui/components/page-header";
import { MediaHeader } from "../components/media-header";
import { MediaGrid, MediaTable } from "../components/media-library-views";
import { MediaEditDialog } from "../components/media-edit-dialog";
import {
  deleteMedia,
  fetchMedia,
  fetchMediaUsage,
  mediaDisplayName,
  updateMedia,
  uploadMedia,
  type MediaItem,
  type MediaUsageEntry,
} from "../lib/media";
import type { ClientOutletContext } from "../lib/outlet-context";

const USAGE_LABEL: Record<MediaUsageEntry["type"], string> = {
  gallery: "gallery",
  page: "page",
  logo: "site logo",
  favicon: "site favicon",
  menu_item: "menu item",
};

function describeUsage(usage: MediaUsageEntry[]): string {
  return usage
    .map((entry) =>
      entry.type === "logo" || entry.type === "favicon"
        ? `the ${USAGE_LABEL[entry.type]}`
        : `${USAGE_LABEL[entry.type]} "${entry.label}"`
    )
    .join(", ");
}

interface DeleteTarget {
  items: MediaItem[];
  /** item id -> where it's used (empty array = unused). */
  usage: Record<string, MediaUsageEntry[]>;
}

export function MediaLibraryPage() {
  const { api, refreshStatus } = useOutletContext<ClientOutletContext>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const controls = useListControls();
  const query = useApiQuery(
    () => (api ? fetchMedia(api, controls.queryString) : null),
    [api, controls.queryString]
  );
  const items = query.data?.media ?? null;
  const assetsBaseUrl = query.data?.assetsBaseUrl ?? null;
  const total = query.data?.total ?? 0;
  const [error, setError] = useState("");
  const [view, setView] = useState<"grid" | "table">("grid");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [editTarget, setEditTarget] = useState<MediaItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [checkingUsage, setCheckingUsage] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      items && prev.size < items.length ? new Set(items.map((item) => item.id)) : new Set()
    );
  };

  const uploadFiles = async (files: File[]) => {
    if (!api || !files.length) return;
    setUploading(true);
    setError("");
    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress(files.length > 1 ? `Uploading ${i + 1} of ${files.length}…` : "Uploading…");
        await uploadMedia(api, files[i]);
      }
      // Newest-first server ordering puts fresh uploads at the top of page 1.
      controls.setPage(1);
      query.reload();
      refreshStatus?.();
    } catch (err) {
      setError(`Upload failed: ${(err as Error).message}`);
    } finally {
      setUploading(false);
      setUploadProgress("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const saveFields = async (
    item: MediaItem,
    fields: { title: string; caption: string; alt?: string }
  ) => {
    if (!api) return;
    setSaving(true);
    setError("");
    const payload = { title: fields.title, caption: fields.caption, alt: fields.alt ?? item.alt };
    try {
      await updateMedia(api, item.id, payload);
      query.setData((prev) =>
        prev
          ? {
              ...prev,
              media: prev.media.map((existing) =>
                existing.id === item.id ? { ...existing, ...payload } : existing
              ),
            }
          : prev
      );
      setEditTarget(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Delete flow: look up usage first so the confirm dialog can warn exactly
  // where each file still appears.
  const startDelete = async (targets: MediaItem[]) => {
    if (!api || !targets.length) return;
    setCheckingUsage(true);
    setError("");
    try {
      const usageEntries = await Promise.all(
        targets.map(async (item) => {
          const result = await fetchMediaUsage(api, item.id);
          return [item.id, result.usage] as const;
        })
      );
      setDeleteTarget({ items: targets, usage: Object.fromEntries(usageEntries) });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCheckingUsage(false);
    }
  };

  const confirmDelete = async () => {
    if (!api || !deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      for (const item of deleteTarget.items) {
        await deleteMedia(api, item.id);
      }
      const deletedIds = new Set(deleteTarget.items.map((item) => item.id));
      query.reload();
      setSelected((prev) => new Set([...prev].filter((id) => !deletedIds.has(id))));
      setDeleteTarget(null);
      setEditTarget(null);
      refreshStatus?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const usedItems = deleteTarget
    ? deleteTarget.items.filter((item) => (deleteTarget.usage[item.id] ?? []).length > 0)
    : [];

  return (
    <div>
      <MediaHeader />
      <PageHeader
        title="Media"
        description="Every file you've uploaded, in one place. Reuse them anywhere your site shows an image."
        actions={
          <div className="flex items-center gap-2">
            {uploadProgress && <p className="text-sm text-muted-foreground">{uploadProgress}</p>}
            {selected.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive"
                disabled={checkingUsage || deleting}
                onClick={() => {
                  void startDelete((items ?? []).filter((item) => selected.has(item.id)));
                }}
              >
                {checkingUsage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete selected ({selected.size})
              </Button>
            )}
            <div className="inline-flex rounded-md border p-0.5">
              <button
                type="button"
                onClick={() => setView("grid")}
                aria-label="Grid view"
                className={`rounded px-2 py-1 ${view === "grid" ? "bg-muted" : "text-muted-foreground"}`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setView("table")}
                aria-label="Table view"
                className={`rounded px-2 py-1 ${view === "table" ? "bg-muted" : "text-muted-foreground"}`}
              >
                <Rows3 className="h-4 w-4" />
              </button>
            </div>
            <Button size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif,.svg,.pdf"
              multiple
              className="hidden"
              onChange={(e) => {
                void uploadFiles(Array.from(e.target.files ?? []));
              }}
            />
          </div>
        }
      />

      {(error || query.error) && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error || query.error}</AlertDescription>
        </Alert>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={controls.search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => controls.setSearch(e.target.value)}
          placeholder="Search media…"
          className="pl-8"
          aria-label="Search media"
        />
      </div>

      {!items && !query.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {items && items.length === 0 && (
        <Card>
          <CardContent className="py-14 text-center text-muted-foreground">
            {controls.q
              ? "No files match your search."
              : "Nothing here yet — click \"Upload\" to add images. Photos are resized automatically so your site stays fast."}
          </CardContent>
        </Card>
      )}

      {items && items.length > 0 && view === "grid" && (
        <MediaGrid
          items={items}
          assetsBaseUrl={assetsBaseUrl}
          selected={selected}
          onToggleSelect={toggleSelect}
          onOpen={setEditTarget}
        />
      )}

      {items && items.length > 0 && view === "table" && (
        <Card>
          <CardContent className="p-0">
            <MediaTable
              items={items}
              assetsBaseUrl={assetsBaseUrl}
              selected={selected}
              onToggleSelect={toggleSelect}
              onToggleAll={toggleAll}
              onSaveFields={(item, fields) => { void saveFields(item, fields); }}
              onDelete={(item) => { void startDelete([item]); }}
            />
          </CardContent>
        </Card>
      )}

      {items && (
        <PaginationControls
          page={controls.page}
          total={total}
          perPage={controls.perPage}
          onPageChange={controls.setPage}
          onPerPageChange={controls.setPerPage}
          itemsLabel="files"
          className="mt-4"
        />
      )}

      <MediaEditDialog
        item={editTarget}
        assetsBaseUrl={assetsBaseUrl}
        saving={saving}
        onSave={(item, fields) => { void saveFields(item, fields); }}
        onDelete={(item) => { void startDelete([item]); }}
        onClose={() => setEditTarget(null)}
      />

      {/* Delete confirm, listing exactly where still-used files appear. */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.items.length === 1
                ? `"${mediaDisplayName(deleteTarget.items[0])}"`
                : `${deleteTarget?.items.length ?? 0} files`}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {usedItems.length > 0 && (
                  <ul className="list-disc space-y-1 pl-5">
                    {usedItems.map((item) => (
                      <li key={item.id}>
                        <strong>{mediaDisplayName(item)}</strong> is used in{" "}
                        {describeUsage(deleteTarget?.usage[item.id] ?? [])} — it will disappear from
                        there.
                      </li>
                    ))}
                  </ul>
                )}
                <p>
                  {usedItems.length > 0
                    ? "Deleted files are removed from storage and can't be recovered."
                    : "The files aren't used anywhere. They're removed from storage and can't be recovered."}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e: React.MouseEvent) => { e.preventDefault(); void confirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
